import React, { useState } from 'react'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { getPasswordStrength } from '@lib/validation'

const PasswordInput = ({
  id,
  value,
  onChange,
  placeholder = 'Enter password',
  label = 'Password',
  showStrengthIndicator = false,
  required = false,
  disabled = false,
  error = null
}) => {
  const [showPassword, setShowPassword] = useState(false)
  const strength = showStrengthIndicator ? getPasswordStrength(value) : null

  const strengthColors = {
    none: '',
    weak: 'bg-red-500 dark:bg-red-600',
    medium: 'bg-yellow-500 dark:bg-yellow-600',
    strong: 'bg-green-500 dark:bg-green-600',
    'very-strong': 'bg-emerald-500 dark:bg-emerald-600'
  }

  const strengthLabels = {
    none: '',
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    'very-strong': 'Very Strong'
  }

  return (
    <div className="w-full">
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
      >
        {label}
        {required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
      </label>

      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          id={id}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full px-3 py-2 pr-10 border ${
            error
              ? 'border-red-500 dark:border-red-400'
              : 'border-gray-300 dark:border-gray-600'
          } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
          required={required}
          disabled={disabled}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          disabled={disabled}
          tabIndex={-1}
        >
          {showPassword ? (
            <EyeSlashIcon className="w-5 h-5" />
          ) : (
            <EyeIcon className="w-5 h-5" />
          )}
        </button>
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {showStrengthIndicator && value && strength && (
        <div className="mt-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${strengthColors[strength.strength]}`}
                style={{ width: `${(strength.score / 7) * 100}%` }}
              />
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[80px]">
              {strengthLabels[strength.strength]}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default PasswordInput
